import type { IBasePerson } from './common'
import type { IStudent } from './family'
import type { IPermittedUse, ISpace } from './space'
import type { IStaff } from './staff'

export type AttendanceMedicalCondition = {
  name: string
}

export type AttendanceMedication = {
  administrationInfo: { startDate: string; endDate: string }
}

export interface IWarning {
  title: string
  description: string
}

export enum WarningLevel {
  Positive = 'positive',
  Negative = 'negative',
  Warning = 'warning',
}

export enum WarningLevelInteger {
  Positive = -1,
  Negative = 1,
  Warning = 0,
}

export const WARNING_LEVEL_MAP = new Map([
  [WarningLevelInteger.Positive, WarningLevel.Positive],
  [WarningLevelInteger.Negative, WarningLevel.Negative],
  [WarningLevelInteger.Warning, WarningLevel.Warning],
])

export interface ISpaceInfo {
  id: string
  color: string
  name: string
  attendanceId: string
  teams: ITeam[]
  noOfTeams: number
  staff: IAttendee[]
  noOfStaff: number
  students: IAttendee[]
  noOfStudents: number
  attendanceWarnings: IWarning[]
  isNapping: boolean
  hasExtraStaff: boolean
  isEmpty: boolean
  warningColor?: WarningLevel
  original: ISpace
}

export interface IAttendee extends IBasePerson {
  familyId: string
  preferredName: string
  medicalConditions: AttendanceMedicalCondition[]
  medications: AttendanceMedication[]
  prohibitedFoods: string[]
  school: {
    id: string
  }
  grade: string
  meta: null
  group: {
    id: string
    name: string
  } | null
  dateTime: string
  taskId: string
  pod: {
    id: string
    name: string
  } | null
  isMealService?: boolean
  isDropIn?: boolean
}

interface ITeamRatio {
  name: string
  standardRatio: number
  nappingRatio: number
  maxTeamSize: number
  licenseType: string
}

export interface ITeam {
  id: string
  name: string
  license: string
  teamRatio: ITeamRatio
  attendees: IAttendee[]
  teamWarnings: IWarning[]
  groupNames?: string
}

export interface IAttendance {
  id: string
  space: ISpace
  teams: ITeam[]
  isNapping: boolean
  permittedUse: IPermittedUse
  attendanceWarnings: IWarning[]
}

export interface IAttendeeNode {
  id: string
  label: string
  avatar: string
  familyId: string
  spaceId?: string
  selected: boolean
  type: string
  preferredName: string
  firstName: string
  lastName: string
  middleName: string
  dateOfBirth: string
  medicalConditions: AttendanceMedicalCondition[]
  medications: AttendanceMedication[]
  prohibitedFoods: string[]
  authorizedMember?: any
  group: {
    id: string
    name: string
  } | null
  school: {
    id: string
  } | null
  grade: string
  pod: {
    id: string
    name: string
  } | null
  taskId?: string
  isDropIn?: boolean
}

export interface ITeamNode {
  id: string
  label: string
  spaceId?: string
  taskId: string
  attendanceId: string
  ratio: number
  ratioLicense: string
  ratioName: string
  maxCapacity: number
  selected: boolean
  tickable?: boolean
  type: string
  disabled: boolean
  children: IAttendeeNode[]
  teamWarnings: IWarning[]
}

export interface IAttendanceNode {
  id: string
  label: string
  color: string
  spaceId?: string
  spaceType?: string
  taskId: string
  isNapping: boolean
  attendanceId: string
  selected: boolean
  disabled: boolean
  tickable?: boolean
  type: string
  children: ITeamNode[]
  license: string
  maxCapacity: number
  attendanceWarnings: IWarning[]
}

export interface IAttendanceLogItem {
  id: string
  logTime: string
  spaceId: string
  type: string
  taskId: string
  span: string
  space?: ISpace
  approver: { id: string; name: string }
  authorizedMember: { id?: string; name: string } | null
  isApprovalRequired?: boolean
  approvalStatus?: string
  approvedBy?: string
  overTimeMinutes?: number
  typicalShift?: { start: string; end: string } | null
}

export interface IAttendanceLog {
  id?: string
  familyId?: string
  attendeeId: string
  attendeeType: string
  logs: IAttendanceLogItem[]
  logDate?: string
  duration?: number
  logTime: string
  spaceId: string
  type: string
  taskId: string
  span: string
  space?: ISpace
  approver: { id: string; name: string }
  authorizedMember: { id?: string; name: string } | null
  isApprovalRequired?: boolean
  approvalStatus?: string | null
  overTimeMinutes?: number
  typicalShift?: { start: string; end: string } | null
  signOutType?: string
  attendance?: IAttendance
  convertedLogTime?: string
  authorizedMemberId?: string
  team: { id: string; name: string } | null
  teamId?: string
  attendee: IAttendee
  partitionKey?: string
  localTime?: string
}

export interface IAttendanceLogDraftStudentView extends IAttendanceLog {
  student?: IStudent
  spaceName?: string
}
export interface IAttendanceLogDraftStaffView extends IAttendanceLog {
  staff?: IStaff
  spaceName?: string
}

export interface IAttendanceFilter {
  groups: string[]
  schools: string[]
  grades: string[]
  spaces: string[]
  pods: string[]
}

export interface IAttendanceStaffFilter {
  groups: string[]
}

export interface ITimeInOut {
  inTime: string
  outTime: string
  inTimeUtc: string
  outTimeUtc: string
}

export interface IAttendanceLogRecords {
  id?: string
  logDate: string
  convertedLogTime: string
  logId: string
  attendeeId?: string
  familyId: string
  firstName: string
  lastName: string
  type: string
  ageYear: number
  ageMonth: number
  ageInMonths: number
  timeStr: string
  action: string
  space: string
  authorization: string
  noOfStaffInSpace: number
  noOfChildrenInSpace: number
  requiredSpaceRatio: string
  actualSpaceRatio: string
  teams: ITeam[]
  standardRatio?: number
  isNapping: boolean
  inCompliance: boolean
}

export interface IAuthorizedMember {
  id: string
  name: string
  type: string
  relationship: string
  pickupAuthorization: string
  pickupActions: string[]
}

export interface IStudentAuthorizedMembers {
  studentId: string
  authorizedMembers: IAuthorizedMember[]
}
